import type {
    LogEntry,
    LogFilters,
    LogsResponse,
    LogWindowParams,
    LogWindowResponse,
    PaginationParams,
    ProjectSummary
} from '../types/index';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export class LogsApiError extends Error {
    constructor(
        message: string,
        public statusCode?: number,
        public code?: string
    ) {
        super(message);
        this.name = 'LogsApiError';
    }
}

// Helper function to get auth headers with JWT token
const getAuthHeaders = (): Record<string, string> => {
    const token = localStorage.getItem('logwatch_token');
    return {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
    };
};

const handleResponse = async (response: Response) => {
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new LogsApiError(
            errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`,
            response.status,
            errorData.error?.code
        );
    }
    return response.json();
};

export const logsApi = {
    // Fetch logs with filtering and pagination
    async getLogs(
        filters: LogFilters = {},
        pagination: PaginationParams = { page: 1, pageSize: 50 },
        signal?: AbortSignal
    ): Promise<LogsResponse> {
        const params = new URLSearchParams();

        // Add pagination
        params.append('page', pagination.page.toString());
        params.append('pageSize', pagination.pageSize.toString());

        // Add filters based on new schema
        if (filters.search) {
            params.append('search', filters.search);
        }
        if (filters.level) {
            if (Array.isArray(filters.level)) {
                params.append('level', filters.level.join(','));
            } else {
                params.append('level', filters.level);
            }
        }
        if (filters.project) {
            if (Array.isArray(filters.project)) {
                params.append('project', filters.project.join(','));
            } else {
                params.append('project', filters.project);
            }
        }
        if (filters.service) {
            params.append('service', filters.service);
        }
        if (filters.category) {
            params.append('category', filters.category);
        }
        if (filters.env) {
            params.append('env', filters.env);
        }
        if (filters.host) {
            params.append('host', filters.host);
        }
        if (filters.dateFrom) {
            params.append('dateFrom', filters.dateFrom);
        }
        if (filters.dateTo) {
            params.append('dateTo', filters.dateTo);
        }
        if (filters.tags && filters.tags.length > 0) {
            params.append('tags', filters.tags.join(','));
        }
        if (filters.severityThreshold) {
            if (Array.isArray(filters.severityThreshold)) {
                params.append('severityThreshold', filters.severityThreshold.join(','));
            } else {
                params.append('severityThreshold', filters.severityThreshold.toString());
            }
        }

        const response = await fetch(`${API_BASE_URL}/logs?${params.toString()}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            signal
        });

        return handleResponse(response);
    },

    // Get log statistics
    async getLogStats(): Promise<{
        totalLogs: number;
        logLevels: Record<string, number>;
        projects: Record<string, number>;
        services: Record<string, number>;
        categories: Record<string, number>;
        environments: Record<string, number>;
        lastUpdated: string;
    }> {
        const response = await fetch(`${API_BASE_URL}/logs/stats`, {
            method: 'GET',
            headers: getAuthHeaders()
        });

        return handleResponse(response);
    },

    async getProjects(signal?: AbortSignal): Promise<ProjectSummary[]> {
        const response = await fetch(`${API_BASE_URL}/logs/projects`, {
            method: 'GET',
            headers: getAuthHeaders(),
            signal
        });

        return handleResponse(response);
    },

    // Fetch logs incrementally with cursor-based pagination
    async getIncrementalLogs(
        filters: LogFilters = {},
        params: { afterId?: string; beforeId?: string; limit?: number } = {},
        signal?: AbortSignal
    ): Promise<{
        logs: LogEntry[];
        hasMore: boolean;
        newestId?: string;
        oldestId?: string;
    }> {
        const queryParams = new URLSearchParams();

        // Add cursor parameters
        if (params.afterId) {
            queryParams.append('afterId', params.afterId);
        }
        if (params.beforeId) {
            queryParams.append('beforeId', params.beforeId);
        }
        if (params.limit) {
            queryParams.append('limit', params.limit.toString());
        }

        // Add filters (same as getLogs)
        if (filters.search) {
            queryParams.append('search', filters.search);
        }
        if (filters.level) {
            if (Array.isArray(filters.level)) {
                queryParams.append('level', filters.level.join(','));
            } else {
                queryParams.append('level', filters.level);
            }
        }
        if (filters.project) {
            if (Array.isArray(filters.project)) {
                queryParams.append('project', filters.project.join(','));
            } else {
                queryParams.append('project', filters.project);
            }
        }
        if (filters.service) {
            queryParams.append('service', filters.service);
        }
        if (filters.category) {
            queryParams.append('category', filters.category);
        }
        if (filters.env) {
            queryParams.append('env', filters.env);
        }
        if (filters.host) {
            queryParams.append('host', filters.host);
        }
        if (filters.dateFrom) {
            queryParams.append('dateFrom', filters.dateFrom);
        }
        if (filters.dateTo) {
            queryParams.append('dateTo', filters.dateTo);
        }
        if (filters.tags && filters.tags.length > 0) {
            queryParams.append('tags', filters.tags.join(','));
        }
        if (filters.severityThreshold) {
            if (Array.isArray(filters.severityThreshold)) {
                queryParams.append('severityThreshold', filters.severityThreshold.join(','));
            } else {
                queryParams.append('severityThreshold', filters.severityThreshold.toString());
            }
        }

        const response = await fetch(`${API_BASE_URL}/logs/incremental?${queryParams.toString()}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            signal
        });

        return handleResponse(response);
    },

    async getLogWindow(filters: LogFilters = {}, params: LogWindowParams = {}, signal?: AbortSignal): Promise<LogWindowResponse> {
        const queryParams = new URLSearchParams();

        if (params.cursor) {
            queryParams.append('cursor', params.cursor);
        }

        if (params.direction) {
            queryParams.append('direction', params.direction);
        }

        if (params.limit) {
            queryParams.append('limit', params.limit.toString());
        }

        if (filters.search) {
            queryParams.append('search', filters.search);
        }
        if (filters.level) {
            if (Array.isArray(filters.level)) {
                queryParams.append('level', filters.level.join(','));
            } else {
                queryParams.append('level', filters.level);
            }
        }
        if (filters.project) {
            if (Array.isArray(filters.project)) {
                queryParams.append('project', filters.project.join(','));
            } else {
                queryParams.append('project', filters.project);
            }
        }
        if (filters.service) {
            queryParams.append('service', filters.service);
        }
        if (filters.category) {
            queryParams.append('category', filters.category);
        }
        if (filters.env) {
            queryParams.append('env', filters.env);
        }
        if (filters.host) {
            queryParams.append('host', filters.host);
        }
        if (filters.dateFrom) {
            queryParams.append('dateFrom', filters.dateFrom);
        }
        if (filters.dateTo) {
            queryParams.append('dateTo', filters.dateTo);
        }
        if (filters.tags && filters.tags.length > 0) {
            queryParams.append('tags', filters.tags.join(','));
        }
        if (filters.severityThreshold) {
            if (Array.isArray(filters.severityThreshold)) {
                queryParams.append('severityThreshold', filters.severityThreshold.join(','));
            } else {
                queryParams.append('severityThreshold', filters.severityThreshold.toString());
            }
        }

        const response = await fetch(`${API_BASE_URL}/logs/window?${queryParams.toString()}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            signal
        });

        return handleResponse(response);
    },

    // Health check
    async checkHealth(signal?: AbortSignal): Promise<{
        status: string;
        timestamp: string;
        uptime: number;
        responseTime: number;
        version: string;
        mode?: string;
        services: {
            clickhouse: {
                status: string;
                url: string;
                mockMode?: boolean;
            };
        };
        memory: {
            used: number;
            total: number;
            unit: string;
        };
    }> {
        const response = await fetch(`${API_BASE_URL}/health`, {
            method: 'GET',
            headers: getAuthHeaders(),
            signal
        });

        return handleResponse(response);
    }
};
